import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LOGO_URL } from "../data/siteData";

export default function AdminForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to send reset link");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] px-6 py-12" data-testid="admin-forgot-page">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="text-center mb-8">
                        <img src={LOGO_URL} alt="Anand Tutorials" className="h-20 mx-auto mb-4" />
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">
                            <Lock className="w-3.5 h-3.5" /> Password Recovery
                        </div>
                        <h1 className="font-heading text-2xl font-bold mt-2">Forgot Your Password?</h1>
                        <p className="text-sm text-slate-500 mt-2">
                            Enter the email associated with your admin account and we'll email you a secure link to reset your password.
                        </p>
                    </div>

                    {sent ? (
                        <div className="text-center space-y-4 py-4" data-testid="forgot-success">
                            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h3 className="font-heading text-lg font-bold text-[#0F172A]">Check your inbox</h3>
                            <p className="text-sm text-slate-600">
                                If an admin account with <strong>{email}</strong> exists, a password reset link has been sent. The link will expire in 60 minutes.
                            </p>
                            <p className="text-xs text-slate-400">Didn't get it? Check spam, or wait 60 minutes and try again.</p>
                            <Link to="/admin/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-[#1E3A8A] font-semibold text-sm hover:underline mt-2">
                                <ArrowLeft className="w-4 h-4" /> Back to Sign In
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <label className="block">
                                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Admin Email</span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="forgot-email"
                                    placeholder="admin@anandtutorials.com"
                                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={submitting || !email}
                                data-testid="forgot-submit"
                                className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {submitting ? "Sending..." : "Send Reset Link"}
                            </button>
                            <Link
                                to="/admin/login"
                                data-testid="back-to-login"
                                className="text-xs font-semibold text-[#1E3A8A] hover:underline inline-flex items-center gap-1 justify-center w-full"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
                            </Link>
                        </form>
                    )}
                </div>
                <p className="text-center text-blue-200 text-xs mt-6">
                    © {new Date().getFullYear()} Anand Tutorials · <Link to="/" className="hover:text-amber-300">Back to Site</Link>
                </p>
            </div>
        </div>
    );
}
